import React, { useEffect, useState, createContext, useContext, useCallback } from 'react';
import { captureException as sentryCaptureException } from '@sentry/minimal';

import enMessages from 'src/locales/compiled/en-US.json';
import { captureException } from 'src/shared/utils/captureException';

import {
  detectBrowserLocale,
  getSavedLocale,
  importMessages,
  LocaleMessages,
  saveLocale,
  SupportedLocale,
} from './locales';
import { defaultLocale, ReactIntlProvider } from './reactIntl';
export const defaultMessages = { ...enMessages };

interface LocaleContextValue {
  locale: SupportedLocale;
  setLocale(locale: SupportedLocale): void;
}

const LocaleContext = createContext<LocaleContextValue>({
  locale: 'en-US',
  setLocale: () => {},
});

export function useLocale(): LocaleContextValue {
  const { locale, setLocale } = useContext(LocaleContext);

  return {
    locale,
    setLocale,
  };
}

interface TranslationProviderProps {
  /**
   * Prevents loading any locales other than the default (i.e. English)
   *
   * Helpful for testing to avoid asynchronous updates and unpredictable
   * state.
   */
  useDefaultLocale?: boolean;
}

export const TranslationProvider: React.FC<TranslationProviderProps> = ({
  children,
  useDefaultLocale = false,
}) => {
  const [locale, setLocaleValue] = useState<SupportedLocale | null>(
    () => getSavedLocale() ?? detectBrowserLocale(),
  );
  // 'Messages' are translation strings
  const [messages, setMessages] = useState<LocaleMessages | null>(null);

  const setLocale = useCallback((locale: SupportedLocale) => {
    saveLocale(locale);

    setLocaleValue(locale);
  }, []);

  useEffect(() => {
    if (locale && !useDefaultLocale) {
      importMessages(locale)
        .then(setMessages)
        .catch(error => {
          // Restore the default messages on error to
          // prevent a mismatch between locale and
          // translation strings...
          setLocaleValue(null);
          setMessages(defaultMessages);

          sentryCaptureException(error);
          captureException(error);
        });
    }
  }, [locale, useDefaultLocale]);

  const resolvedLocale = locale ?? defaultLocale;

  return (
    <ReactIntlProvider messages={messages ?? defaultMessages} locale={resolvedLocale}>
      <LocaleContext.Provider value={{ locale: resolvedLocale, setLocale }}>
        {children}
      </LocaleContext.Provider>
    </ReactIntlProvider>
  );
};
